/* AgentMeeting — Meeting history / management page (wired to /api/meetings) */

import React from 'react';
import { useI18n } from '../shared/i18n';
import { MarketingBar } from '../shared/marketing-bar';
import { meetings as meetingsApi, starter, HTTPError, type MeetingRecord } from '../shared/api';
import { useAuth } from '../shared/auth';

type StatusKey = 'all' | 'running' | 'paused' | 'ended' | 'draft';

const STATUS_FILTERS: { key: StatusKey; en: string; zh: string }[] = [
  { key: 'all',     en: 'All',     zh: '全部' },
  { key: 'running', en: 'Running', zh: '进行中' },
  { key: 'paused',  en: 'Paused',  zh: '已暂停' },
  { key: 'ended',   en: 'Ended',   zh: '已结束' },
  { key: 'draft',   en: 'Draft',   zh: '草稿' },
];

const STATUS_BADGE: Record<string, { glyph: string; color: string; label: string; label_zh: string }> = {
  running:  { glyph: '⏵', color: 'var(--judge)',    label: 'running',  label_zh: '进行中' },
  paused:   { glyph: '⏸', color: 'var(--neutral)',  label: 'paused',   label_zh: '已暂停' },
  ended:    { glyph: '◉', color: 'var(--live)',     label: 'ended',    label_zh: '已结束' },
  decided:  { glyph: '◉', color: 'var(--live)',     label: 'decided',  label_zh: '已决议' },
  draft:    { glyph: '○', color: 'var(--fg-3)',     label: 'draft',    label_zh: '草稿' },
  archived: { glyph: '▣', color: 'var(--fg-3)',     label: 'archived', label_zh: '已归档' },
};

function badgeFor(status: string) {
  return STATUS_BADGE[status] || { glyph: '·', color: 'var(--fg-3)', label: status, label_zh: status };
}

function fmtDuration(start: string, end?: string | null): string {
  const startMs = Date.parse(start);
  if (Number.isNaN(startMs)) return '—';
  const endMs = end ? Date.parse(end) : Date.now();
  const totalMin = Math.max(0, Math.floor((endMs - startMs) / 60000));
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

function fmtDate(iso: string, lang: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return lang === 'zh' ? `今天 ${hh}:${mm}` : `Today ${hh}:${mm}`;
  if (isYesterday) return lang === 'zh' ? `昨天 ${hh}:${mm}` : `Yesterday ${hh}:${mm}`;
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mo}/${dd} ${hh}:${mm}`;
}

export function MeetingsHistory({ goTo }: { goTo: (route: string) => void }) {
  const { lang, t } = useI18n();
  const { user } = useAuth();

  const [meetings, setMeetings] = React.useState<MeetingRecord[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<StatusKey>('all');

  // Inline "create new meeting" form (replaces the deleted onboarding wizard).
  const [showCreateForm, setShowCreateForm] = React.useState<boolean>(false);
  const [formTopic, setFormTopic] = React.useState<string>('');
  const [formPack, setFormPack] = React.useState<string>('executive-decision');
  const [formSubmitting, setFormSubmitting] = React.useState<boolean>(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await meetingsApi.list();
      setMeetings(res.meetings || []);
    } catch (err) {
      if (err instanceof HTTPError) {
        if (err.status === 401) {
          setError(lang === 'zh' ? '请先登录' : 'Please sign in to view meetings');
        } else {
          setError(lang === 'zh'
            ? `加载失败 (HTTP ${err.status})`
            : `Failed to load meetings (HTTP ${err.status})`);
        }
      } else {
        setError(lang === 'zh' ? '网络错误, 无法加载会议' : 'Network error — could not load meetings');
      }
    } finally {
      setLoading(false);
    }
  }, [lang]);

  React.useEffect(() => { load(); }, [load]);

  // Auto-open the inline form when the user has no meetings yet — gives a
  // brand-new account something actionable on first arrival.
  React.useEffect(() => {
    if (!loading && !error && meetings.length === 0) {
      setShowCreateForm(true);
    }
  }, [loading, error, meetings.length]);

  const submitCreate = async () => {
    if (!formTopic.trim() || formSubmitting) return;
    setFormSubmitting(true);
    setFormError(null);
    try {
      const r = await starter.seed(formPack, {
        topic: formTopic,
        title: formTopic.slice(0, 40),
      });
      try { localStorage.setItem('agentmeeting.currentMeetingId', r.meeting_id); } catch { /* noop */ }
      goTo('app/agenda');
      location.hash = '#/app/agenda';
    } catch (err: any) {
      const msg = (err && err.body && err.body.error) || (err && err.message) || 'create failed';
      setFormError(msg);
    } finally {
      setFormSubmitting(false);
    }
  };

  const filtered = React.useMemo(() => {
    const q = query.toLowerCase().trim();
    return meetings.filter(m => {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (q) {
        const t = (m.title || '').toLowerCase();
        const tp = (m.topic || '').toLowerCase();
        if (!t.includes(q) && !tp.includes(q)) return false;
      }
      return true;
    });
  }, [meetings, query, statusFilter]);

  const totalSpentCents = React.useMemo(
    () => meetings.reduce((s, m) => s + (m.spent_cents || 0), 0),
    [meetings],
  );

  const onRowClick = (m: MeetingRecord) => {
    try { localStorage.setItem('agentmeeting.currentMeetingId', m.id); } catch { /* noop */ }
    if (m.status === 'running') goTo('app/room');
    else goTo('app/agenda');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)', color: 'var(--fg-0)' }}>
      <MarketingBar here="meetings" goTo={goTo} signedIn={!!user} />

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 28px 80px' }}>
        {/* header */}
        <div className="flex between center wrap" style={{ gap: 16, marginBottom: 28 }}>
          <div>
            <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
              [MEETINGS]
            </div>
            <h1 style={{
              fontSize: 'clamp(28px, 3.4vw, 38px)',
              margin: '6px 0 6px', letterSpacing: '-0.02em', fontWeight: 500,
            }}>
              {t('app.meetings.my')}
            </h1>
            <p style={{ color: 'var(--fg-2)', fontSize: 14, margin: 0 }}>
              {lang === 'zh'
                ? `${meetings.length} 场会议 · 共花费 $${(totalSpentCents / 100).toFixed(2)}`
                : `${meetings.length} meetings · $${(totalSpentCents / 100).toFixed(2)} spent`}
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn ghost mono" onClick={() => goTo('app/replay')}>
              ↻ replay
            </button>
            <button className="btn primary mono"
              onClick={() => {
                setShowCreateForm(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}>
              + {t('app.meetings.new')}
            </button>
          </div>
        </div>

        {/* inline create-meeting form (replaces the onboarding wizard) */}
        {showCreateForm && (
          <div style={{
            border: '1px solid var(--line-2)', borderRadius: 8,
            padding: 24, marginBottom: 24, background: 'var(--bg-1)',
          }}>
            <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.12em' }}>
              [NEW MEETING]
            </div>
            <h3 style={{ fontSize: 18, margin: '8px 0 16px', fontWeight: 500 }}>
              {lang === 'zh' ? '开一场会' : 'Start a meeting'}
            </h3>
            <div className="col gap-3">
              <div>
                <div className="mono tiny dim" style={{ marginBottom: 6, letterSpacing: '0.08em' }}>
                  {lang === 'zh' ? '议题 / TOPIC' : 'TOPIC'}
                </div>
                <textarea
                  className="input"
                  rows={3}
                  placeholder={lang === 'zh' ? '讨论什么主题?' : 'What should the agents debate?'}
                  value={formTopic}
                  onChange={e => setFormTopic(e.target.value)}
                  style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <div className="mono tiny dim" style={{ marginBottom: 6, letterSpacing: '0.08em' }}>
                  {lang === 'zh' ? '角色组合 / PACK' : 'AGENT PACK'}
                </div>
                <select
                  className="input"
                  value={formPack}
                  onChange={e => setFormPack(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="executive-decision">
                    {lang === 'zh'
                      ? '高管决策会 (6 agents · 6 rounds)'
                      : 'Executive decision (6 agents · 6 rounds)'}
                  </option>
                  <option value="red-blue-team">
                    {lang === 'zh'
                      ? '红蓝对抗 (4 agents · 4 rounds)'
                      : 'Red vs blue team (4 agents · 4 rounds)'}
                  </option>
                  <option value="socratic-tutor">
                    {lang === 'zh'
                      ? '苏格拉底诘问 (3 agents · 3 rounds)'
                      : 'Socratic tutor (3 agents · 3 rounds)'}
                  </option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn primary mono"
                  disabled={formSubmitting || !formTopic.trim()}
                  onClick={submitCreate}
                >
                  {formSubmitting
                    ? (lang === 'zh' ? '创建中…' : 'creating…')
                    : (lang === 'zh' ? '创建会议 →' : 'Create meeting →')}
                </button>
                {meetings.length > 0 && (
                  <button
                    className="btn ghost mono"
                    onClick={() => { setShowCreateForm(false); setFormError(null); }}
                  >
                    {lang === 'zh' ? '取消' : 'cancel'}
                  </button>
                )}
              </div>
              {formError && (
                <div className="mono tiny" style={{
                  color: 'var(--err)',
                  padding: '8px 10px', background: 'var(--bg-0)',
                  border: '1px solid var(--err)', borderRadius: 4,
                }}>
                  ! {formError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* search + filters */}
        <div className="flex gap-3 wrap" style={{ marginBottom: 18 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--bg-1)', border: '1px solid var(--line-2)',
            borderRadius: 6, padding: '6px 12px', flex: '1 1 280px', maxWidth: 420,
          }}>
            <span className="mono dim">⌕</span>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('app.meetings.search')}
              style={{
                background: 'transparent', border: 0, outline: 0,
                flex: 1, color: 'var(--fg-0)', fontSize: 13,
                fontFamily: 'var(--mono)',
              }} />
          </div>
          <div className="flex gap-1 wrap">
            {STATUS_FILTERS.map(({ key, en, zh }) => (
              <button key={key} onClick={() => setStatusFilter(key)}
                className="mono"
                style={{
                  padding: '6px 12px',
                  border: '1px solid ' + (statusFilter === key ? 'var(--live)' : 'var(--line-2)'),
                  borderRadius: 100,
                  background: statusFilter === key ? 'var(--live-dim)' : 'transparent',
                  color: statusFilter === key ? 'var(--live)' : 'var(--fg-2)',
                  fontSize: 11, cursor: 'pointer', letterSpacing: '0.04em',
                }}>{lang === 'zh' ? zh : en}</button>
            ))}
          </div>
        </div>

        {/* main panel */}
        <div style={{
          border: '1px solid var(--line-2)', borderRadius: 8, overflow: 'hidden',
          background: 'var(--bg-1)',
        }}>
          {/* table header — only render when we have data */}
          {!loading && !error && filtered.length > 0 && (
            <div className="grid mono tiny" style={{
              gridTemplateColumns: '48px minmax(0,2.5fr) 160px 120px 100px 100px',
              padding: '10px 16px', background: 'var(--bg-2)',
              color: 'var(--fg-3)', letterSpacing: '0.08em', textTransform: 'uppercase',
              borderBottom: '1px solid var(--line-2)',
            }}>
              <div></div>
              <div>{t('app.meetings.motion')}</div>
              <div>{lang === 'zh' ? '时间' : 'when'}</div>
              <div>{t('app.meetings.status')}</div>
              <div>{t('app.meetings.duration')}</div>
              <div>{t('app.meetings.cost')}</div>
            </div>
          )}

          {/* loading */}
          {loading && (
            <div className="col center" style={{ padding: '80px 24px', color: 'var(--fg-3)' }}>
              <span className="mono" style={{ fontSize: 13 }}>$ loading meetings…</span>
            </div>
          )}

          {/* error */}
          {!loading && error && (
            <div className="col center" style={{
              padding: '60px 24px', gap: 14,
              color: 'var(--err, #c33)',
            }}>
              <span className="mono" style={{ fontSize: 13 }}>! {error}</span>
              <button className="btn mono" onClick={() => load()}>
                ↻ {lang === 'zh' ? '重试' : 'retry'}
              </button>
            </div>
          )}

          {/* empty (no meetings at all) — the inline form above is auto-opened
              for fresh accounts, so this slot only renders when the user has
              dismissed the form. */}
          {!loading && !error && meetings.length === 0 && !showCreateForm && (
            <div className="col center" style={{ padding: '80px 24px', gap: 16, color: 'var(--fg-3)' }}>
              <span className="mono" style={{ fontSize: 13 }}>
                {lang === 'zh' ? '还没有会议 — 来开一场吧' : 'No meetings yet — start one'}
              </span>
              <button className="btn primary mono" onClick={() => setShowCreateForm(true)}>
                {lang === 'zh' ? '开一场会 →' : 'Start a meeting →'}
              </button>
            </div>
          )}

          {/* empty (filtered) */}
          {!loading && !error && meetings.length > 0 && filtered.length === 0 && (
            <div className="col center" style={{ padding: 60, color: 'var(--fg-3)' }}>
              <span className="mono">∅ {lang === 'zh' ? '没有匹配的会议' : 'no meetings match'}</span>
            </div>
          )}

          {/* rows */}
          {!loading && !error && filtered.map(m => (
            <MeetingRow key={m.id} m={m} lang={lang} onClick={() => onRowClick(m)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function MeetingRow({
  m, lang, onClick,
}: {
  m: MeetingRecord;
  lang: string;
  onClick: () => void;
}) {
  const status = badgeFor(m.status);
  const cost = `$${((m.spent_cents || 0) / 100).toFixed(2)}`;
  const duration = fmtDuration(m.created_at);
  return (
    <div
      onClick={onClick}
      className="hov"
      style={{
        display: 'grid',
        gridTemplateColumns: '48px minmax(0,2.5fr) 160px 120px 100px 100px',
        gap: 8,
        padding: '14px 16px',
        borderBottom: '1px solid var(--line-1)',
        cursor: 'pointer', alignItems: 'center',
        background: 'transparent',
        borderLeft: '3px solid transparent',
      }}>
      <div className="mono tiny" style={{ color: status.color, letterSpacing: '0.06em' }}>
        {status.glyph}
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="flex center gap-2" style={{ minWidth: 0 }}>
          <span style={{
            fontSize: 14, fontWeight: 500, color: 'var(--fg-0)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{m.title || (lang === 'zh' ? '(未命名)' : '(untitled)')}</span>
          {m.type && (
            <span className="tag mono tiny" style={{ borderColor: 'var(--line-2)', flexShrink: 0 }}>
              {m.type}
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--fg-2)', marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {m.topic || ''}
        </div>
      </div>
      <div className="mono tiny" style={{ color: 'var(--fg-2)' }}>{fmtDate(m.created_at, lang)}</div>
      <div className="mono tiny" style={{ color: status.color }}>
        {status.glyph} {lang === 'zh' ? status.label_zh : status.label}
        {m.total_rounds > 0 && (
          <span className="dim" style={{ marginLeft: 6 }}>
            {m.current_round}/{m.total_rounds}
          </span>
        )}
      </div>
      <div className="mono tiny" style={{ color: 'var(--fg-2)' }}>{duration}</div>
      <div className="mono tiny" style={{ color: 'var(--fg-2)' }}>{cost}</div>
    </div>
  );
}
